// ===============================================================================
// UpgradeMembership.js - Premium Upgrade Page for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/UpgradeMembership.js
// Free Trial Mode - 3 Months Free, No Payment Required
// ===============================================================================

import React, { useState, useEffect } from "react";
import { membershipAPI } from "./api";
import "./UpgradeMembership.css";

function UpgradeMembership({ user, onBack }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  
  // Free trial is always selected
  const [selectedPlan] = useState("3_months");
  
  // Success state
  const [success, setSuccess] = useState(null);

  // ===============================================================================
  // Load on Mount
  // ===============================================================================
  useEffect(() => {
    // Simulate brief loading for smooth UX
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // ===============================================================================
  // Handle Free Trial Activation
  // ===============================================================================
  const handleActivateTrial = async (e) => {
    e.preventDefault();
    setError("");
    setProcessing(true);

    try {
      const res = await membershipAPI.upgrade({
        plan: selectedPlan,
        payment_method: "free_trial",
        card_number: "",
        card_expiry: "",
        card_cvv: "",
        cardholder_name: ""
      });

      if (res.data?.ok) {
        setSuccess({
          message: res.data.message,
          expiresAt: res.data.premium_expires_at,
          totalPaid: 0,
          transactionId: res.data.transaction_id
        });
      }
    } catch (err) {
      console.error("Activation error:", err);
      setError(err?.response?.data?.detail || "Activation failed. Please try again.");
    }

    setProcessing(false);
  };

  // ===============================================================================
  // Free Trial Plan Details
  // ===============================================================================
  const trialPlan = {
    name: "3-Month Free Trial",
    months: 3,
    originalPrice: 3.00,
    discount: 100,
    finalPrice: 0.00
  };

  // ===============================================================================
  // Render Loading
  // ===============================================================================
  if (loading) {
    return (
      <div className="upgrade-page">
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render Success
  // ===============================================================================
  if (success) {
    return (
      <div className="upgrade-page">
        <div className="success-card">
          <div className="success-icon">🎉</div>
          <h1>Welcome to Premium!</h1>
          <p className="success-message">Your free trial has been activated successfully.</p>
          
          <div className="success-details">
            <div className="success-detail">
              <label>Plan</label>
              <span>3-Month Free Trial</span>
            </div>
            <div className="success-detail">
              <label>Valid Until</label>
              <span>{new Date(success.expiresAt).toLocaleDateString()}</span>
            </div>
            <div className="success-detail">
              <label>Amount Charged</label>
              <span>€0.00 (Free)</span>
            </div>
          </div>

          <div className="success-note">
            <span className="note-icon">💡</span>
            <p>Enjoy all premium features for 3 months. We'll notify you before your trial ends.</p>
          </div>

          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Re-login to activate the premium features →
          </button>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render Main Page
  // ===============================================================================
  return (
    <div className="upgrade-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-top-row">
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        </div>
        <h1>⭐ Upgrade to Premium</h1>
        <p className="header-subtitle">Unlock all features and take full control of your expenses</p>
      </div>

      {/* Benefits Section */}
      <div className="benefits-card">
        <h2>Premium Benefits</h2>
        <div className="benefits-grid">
          <div className="benefit-item">
            <span className="benefit-icon">🔍</span>
            <div className="benefit-content">
              <h4>Advanced Search</h4>
              <p>Search expenses across all groups</p>
            </div>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">📊</span>
            <div className="benefit-content">
              <h4>Smart Analytics</h4>
              <p>Detailed spending insights</p>
            </div>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">💼</span>
            <div className="benefit-content">
              <h4>Business Groups</h4>
              <p>Professional expense tracking</p>
            </div>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">🧾</span>
            <div className="benefit-content">
              <h4>Receipt Photos</h4>
              <p>Attach receipts to expenses</p>
            </div>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">♾️</span>
            <div className="benefit-content">
              <h4>Unlimited Groups</h4>
              <p>Create as many groups as needed</p>
            </div>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">📥</span>
            <div className="benefit-content">
              <h4>Export Data</h4>
              <p>PDF and advanced exports</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError("")}>✕</button>
        </div>
      )}

      {/* Plans Section */}
      <div className="plans-section">
        <h2>Choose Your Plan</h2>
        <div className="plans-grid">
          {/* Free Trial Card - Active */}
          <div className="plan-card selected popular">
            <div className="popular-badge">🎁 Limited Offer</div>
            <div className="plan-header">
              <h3>Free Trial</h3>
              <div className="plan-price">
                <span className="price-original">€3.00</span>
                <span className="price">€0.00</span>
              </div>
              <div className="plan-monthly">
                3 months free access
              </div>
            </div>
            <div className="plan-details">
              <div className="plan-savings free-trial-badge">
                🎉 100% OFF - No Credit Card Required
              </div>
              <div className="plan-features">
                <span>✓ All Premium Features</span>
                <span>✓ No Payment Required</span>
                <span>✓ Cancel Anytime</span>
              </div>
            </div>
            <div className="plan-check">✓</div>
          </div>

          {/* 12 Months Card - Disabled */}
          <div className="plan-card disabled">
            <div className="plan-header">
              <h3>Annual Plan</h3>
              <div className="plan-price">
                <span className="price">€9.60</span>
              </div>
              <div className="plan-monthly">
                €0.80/month
              </div>
            </div>
            <div className="plan-details">
              <div className="plan-savings">
                Save 20%
              </div>
              <div className="plan-vat">
                Includes VAT
              </div>
            </div>
            <div className="plan-unavailable">Coming Soon</div>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="order-summary free-trial-summary">
        <h3>Order Summary</h3>
        <div className="summary-row">
          <span>Plan</span>
          <span>{trialPlan.name}</span>
        </div>
        <div className="summary-row">
          <span>Duration</span>
          <span>{trialPlan.months} months</span>
        </div>
        <div className="summary-row">
          <span>Regular Price</span>
          <span className="strikethrough">€{trialPlan.originalPrice.toFixed(2)}</span>
        </div>
        <div className="summary-row discount-row">
          <span>🎁 Free Trial Discount</span>
          <span className="discount-amount">-100%</span>
        </div>
        <div className="summary-row total">
          <span>Total Due Today</span>
          <span className="free-text">FREE</span>
        </div>
      </div>

      {/* Activate Button */}
      <button 
        onClick={handleActivateTrial}
        className="btn btn-premium btn-free-trial"
        disabled={processing}
      >
        {processing ? (
          <>
            <span className="btn-spinner"></span>
            Activating...
          </>
        ) : (
          <>🚀 Activate Free Trial</>
        )}
      </button>

      {/* Note */}
      <div className="security-note free-trial-note">
        <span className="note-icon">✨</span>
        <p>No credit card required. Your trial starts immediately and includes all premium features.</p>
      </div>
    </div>
  );
}

export default UpgradeMembership;

